import { useCallback } from "react";
import * as db from "../lib/db";
import { genId, todayKey } from "../utils/helpers";
import { enqueue, getPendingCount } from "../utils/offline-queue";
import { useToast } from "./useToast";
import { hapticLight } from "../utils/haptics";
import { syncMealToHealth } from "../utils/elevate-health-plugin";
import { track } from "../lib/analytics";

function getCoachMealReaction(meal, servings, mealLog, allMeals, macroGoals) {
  const today = todayKey();
  const todayEntries = (mealLog[today] || []).filter((e) => e.mealId !== "__water__");
  const proteinGoal = macroGoals?.protein || 0;

  // Calculate today's totals BEFORE this meal (it's already been added to state)
  let totalProtein = 0;
  let totalCal = 0;
  for (const entry of todayEntries) {
    const m = (allMeals || []).find((x) => x.id === entry.mealId);
    if (m) {
      totalProtein += (m.protein || 0) * (entry.servings || 1);
      totalCal += (m.calories || 0) * (entry.servings || 1);
    }
  }

  // Add the meal just logged
  const addedProtein = (meal.protein || 0) * servings;
  const addedCal = (meal.calories || 0) * servings;
  totalProtein += addedProtein;
  totalCal += addedCal;

  const proteinPct = proteinGoal > 0 ? Math.round((totalProtein / proteinGoal) * 100) : 0;
  const proteinLeft = proteinGoal > 0 ? Math.max(0, proteinGoal - totalProtein) : 0;

  // High-protein meal
  if (addedProtein >= 30 && proteinGoal > 0) {
    if (proteinPct >= 90) return `${meal.name} — ${addedProtein}g protein. You've hit your target.`;
    return `${meal.name} — ${addedProtein}g protein. ${proteinPct}% of target.`;
  }

  // Target hit
  if (proteinGoal > 0 && proteinPct >= 100) {
    return `${meal.name} logged. Protein target hit.`;
  }

  // Close to target
  if (proteinGoal > 0 && proteinPct >= 75) {
    return `${meal.name} logged. ${proteinLeft}g protein to go.`;
  }

  // Default with calorie context
  if (totalCal > 0) {
    return `${meal.name} logged. ${totalCal} cal today.`;
  }

  return `${meal.name} logged`;
}

export function useMealActions({ userId, mealLog, isOnline, setMealLog, setCustomMeals, setPendingSyncs, allMeals, macroGoals }) {
  const toast = useToast();

  const logMeal = useCallback(async (meal, servings = 1, ts) => {
    const today = todayKey();
    const entry = { id: genId(), mealId: meal.id, ts: ts || Date.now(), servings };
    setMealLog((prev) => ({
      ...prev,
      [today]: [...(prev[today] || []), entry],
    }));
    hapticLight();
    track("meal_logged", { calories: Math.round((meal.calories || 0) * servings) });
    // Coach-aware feedback
    const coachMsg = getCoachMealReaction(meal, servings, mealLog, allMeals, macroGoals);
    toast.success(coachMsg, 3500);
    syncMealToHealth(meal, servings).catch((e) => console.error("HealthKit meal sync failed:", e));
    try {
      await db.insertMealEntry(userId, today, entry);
    } catch (e) {
      console.error("Failed to log meal:", e);
      if (!isOnline) {
        enqueue({ type: "meal", payload: { userId, date: today, entry } });
        setPendingSyncs(getPendingCount());
      } else {
        setMealLog((prev) => ({
          ...prev,
          [today]: (prev[today] || []).filter((x) => x.id !== entry.id),
        }));
        toast.error("Failed to save meal — please try again");
      }
    }
  }, [userId, isOnline, setMealLog, setPendingSyncs, toast]);

  const removeMealEntry = useCallback(async (id) => {
    const today = todayKey();
    let removedEntry = null;
    setMealLog((prev) => {
      removedEntry = (prev[today] || []).find((e) => e.id === id);
      return {
        ...prev,
        [today]: (prev[today] || []).filter((e) => e.id !== id),
      };
    });
    try {
      await db.deleteMealEntry(id);
    } catch (e) {
      console.error("Failed to remove meal entry:", e);
      toast.error("Failed to remove meal entry");
      if (removedEntry) {
        setMealLog((prev) => ({
          ...prev,
          [today]: [...(prev[today] || []), removedEntry],
        }));
      }
    }
  }, [setMealLog, toast]);

  const updateMealServings = useCallback(async (entryId, newServings) => {
    const today = todayKey();
    let oldServings = null;
    setMealLog((prev) => {
      oldServings = (prev[today] || []).find((e) => e.id === entryId)?.servings ?? null;
      return {
        ...prev,
        [today]: (prev[today] || []).map((e) =>
          e.id === entryId ? { ...e, servings: newServings } : e
        ),
      };
    });
    try {
      await db.updateMealServings(entryId, newServings);
    } catch (e) {
      console.error("Failed to update servings:", e);
      if (oldServings !== null) {
        setMealLog((prev) => ({
          ...prev,
          [today]: (prev[today] || []).map((x) =>
            x.id === entryId ? { ...x, servings: oldServings } : x
          ),
        }));
      }
    }
  }, [setMealLog]);

  const quickAddMeal = useCallback(async (mealData) => {
    const meal = { id: genId(), ...mealData, description: "", category: "Quick Add" };
    setCustomMeals((prev) => [...prev, meal]);
    try {
      await db.insertCustomMeal(userId, meal);
    } catch (e) {
      console.error("Failed to save quick-add meal:", e);
      toast.error("Failed to save meal");
      // Roll back and bail — logging now would create a meal_log entry pointing
      // at a custom meal that doesn't exist, which reads back as 0 calories.
      setCustomMeals((prev) => prev.filter((m) => m.id !== meal.id));
      return;
    }
    logMeal(meal, 1);
  }, [userId, setCustomMeals, logMeal, toast]);

  const saveAndLogApiFood = useCallback(async (foodData, servings = 1) => {
    const meal = {
      id: genId(),
      name: foodData.name,
      description: foodData.brand
        ? `${foodData.brand}${foodData.serving_size ? ` · ${foodData.serving_size}` : ""}`
        : (foodData.serving_size || ""),
      category: "Other",
      calories: foodData.calories,
      protein: foodData.protein,
      carbs: foodData.carbs,
      fat: foodData.fat,
      fiber: foodData.fiber || 0,
    };
    setCustomMeals((prev) => [...prev, meal]);
    try {
      await db.insertCustomMeal(userId, meal);
    } catch (e) {
      console.error("Failed to save food database item as custom meal:", e);
      toast.error("Failed to save food");
      setCustomMeals((prev) => prev.filter((m) => m.id !== meal.id));
      return;
    }
    logMeal(meal, servings);
  }, [userId, setCustomMeals, logMeal, toast]);

  return { logMeal, removeMealEntry, updateMealServings, quickAddMeal, saveAndLogApiFood };
}
